import { KpiAppUsageSource } from '@/types/response';

/**
 * 画面（GA4 のスクリーンクラス）・機能（AppsFlyer のイベント名）の日本語名と分類。
 * 対応表に無い名前は元の名前のまま「その他」に入れる（アプリに画面が増えても取り込みは止めない）。
 */
export const CATEGORIES = [
  'ホーム',
  '日記',
  '家族',
  '家族のいま',
  '歩数・健康',
  '脳トレ',
  'そなえ',
  'かぞくごと・あゆみ',
  '防犯',
  'マイページ・設定',
  '登録・ログイン',
  'その他',
] as const;
export type Category = (typeof CATEGORIES)[number];

type Entry = [label: string, category: Category];

const SCREENS: Record<string, Entry> = {
  HomeScreen: ['ホーム', 'ホーム'],
  HomeDetailScreen: ['ホーム詳細', 'ホーム'],
  NoticeScreen: ['お知らせ', 'ホーム'],
  DetailNoticeScreen: ['お知らせ詳細', 'ホーム'],
  NewsDetailScreen: ['ニュース詳細', 'ホーム'],
  CalendarScreen: ['カレンダー', 'ホーム'],
  ZodiacSignDetailScreen: ['星座占い', 'ホーム'],
  MenuScreen: ['メニュー', 'ホーム'],

  TimelineTab: ['タイムライン', '日記'],
  CreatePostIntroScreen: ['日記作成の案内', '日記'],
  PostCreationScreen: ['日記作成', '日記'],
  PostWaitingCreateScreen: ['日記の生成待ち', '日記'],
  PostPreviewScreen: ['日記プレビュー', '日記'],
  PostDetailScreen: ['日記詳細', '日記'],
  PostEditScreen: ['日記編集', '日記'],
  IntroductionPostDetailScreen: ['お手本の日記', '日記'],

  FamilyNameScreen: ['家族名の入力', '家族'],
  FamilyInfoScreen: ['家族情報', '家族'],
  GroupListScreen: ['家族一覧', '家族'],
  GroupCreationScreen: ['家族作成', '家族'],
  GroupDetailScreen: ['家族詳細', '家族'],
  GroupEditScreen: ['家族編集', '家族'],
  GroupManagementScreen: ['家族の管理', '家族'],
  GroupSettingsScreen: ['家族の設定', '家族'],
  GroupInvitationScreen: ['家族招待', '家族'],
  FamilyInviteScreen: ['招待の受け取り', '家族'],
  FamilyInviteCodeScreen: ['招待コード入力', '家族'],

  FamilyNowTopScreen: ['家族のいま', '家族のいま'],
  DrivingMemberScreen: ['運転の記録（メンバー）', '家族のいま'],
  DrivingDayScreen: ['運転の記録（日別）', '家族のいま'],
  DrivingRecordSettingsScreen: ['運転の記録の設定', '家族のいま'],

  MonthlyStepsReportScreen: ['歩数の月間レポート', '歩数・健康'],
  HealthDateHistoryScreen: ['健康の記録', '歩数・健康'],
  HealthcareSettingsScreen: ['ヘルスケア設定', '歩数・健康'],

  GameMenuScreen: ['脳トレメニュー', '脳トレ'],
  ListGameScreen: ['脳トレ一覧', '脳トレ'],
  CogEvoScreen: ['CogEvo', '脳トレ'],
  CogEvoPreviewScreen: ['CogEvo 紹介', '脳トレ'],
  CogEvoIllustrationsScreen: ['CogEvo イラスト', '脳トレ'],
  WebViewBWTScreen: ['脳トレ（BWT）', '脳トレ'],
  UknowScreen: ['のうKNOW', '脳トレ'],
  UknowPreviewScreen: ['のうKNOW 紹介', '脳トレ'],
  WebViewUknowScreen: ['のうKNOW（Web）', '脳トレ'],

  PrepareScreen: ['そなえ', 'そなえ'],
  ArticleListScreen: ['記事一覧', 'そなえ'],
  ArticleDetailScreen: ['記事詳細', 'そなえ'],
  CompanyArticleScreen: ['団体の記事', 'そなえ'],
  OfferListScreen: ['オファー一覧', 'そなえ'],
  OfferListByTagScreen: ['オファー一覧（タグ）', 'そなえ'],
  OfferDetailScreen: ['オファー詳細', 'そなえ'],
  EventDetailScreen: ['イベント詳細', 'そなえ'],
  QuizListScreen: ['クイズ一覧', 'そなえ'],
  QuizScreen: ['クイズ', 'そなえ'],
  QuizDetailScreen: ['クイズ詳細', 'そなえ'],
  QuizDetailScreenByCategory: ['クイズ詳細（カテゴリ）', 'そなえ'],
  QuizResultScreen: ['クイズ結果', 'そなえ'],
  PartnerListScreen: ['パートナー一覧', 'そなえ'],
  PartnerDetailScreen: ['パートナー詳細', 'そなえ'],
  LifestyleDiagnosisDetailScreen: ['生活習慣診断', 'そなえ'],
  WhatIfCalculationScreen: ['もしも計算', 'そなえ'],
  WhatIfCalculationResultScreen: ['もしも計算の結果', 'そなえ'],

  KazokugotoDetailScreen: ['かぞくごと詳細', 'かぞくごと・あゆみ'],
  KazokugotoCalendarScreen: ['かぞくごとカレンダー', 'かぞくごと・あゆみ'],
  AyumiTopicListScreen: ['あゆみ一覧', 'かぞくごと・あゆみ'],
  AyumiTopicDetailScreen: ['あゆみ詳細', 'かぞくごと・あゆみ'],

  DetailCrimePreventionScreen: ['防犯情報詳細', '防犯'],
  SettingsCrimePreventionScreen: ['防犯情報の設定', '防犯'],

  MyPageScreen: ['マイページ', 'マイページ・設定'],
  UserProfileScreen: ['ユーザープロフィール', 'マイページ・設定'],
  ProfileScreen: ['プロフィール', 'マイページ・設定'],
  ProfileEditScreen: ['プロフィール編集', 'マイページ・設定'],
  ProfileChangeScreen: ['プロフィール変更', 'マイページ・設定'],
  ProfileAvatarScreen: ['アイコン変更', 'マイページ・設定'],
  ProfileGenderScreen: ['性別の入力', 'マイページ・設定'],
  SettingsScreen: ['設定', 'マイページ・設定'],
  NotificationSettingsScreen: ['通知設定', 'マイページ・設定'],
  ContentSharingSettingsScreen: ['共有設定', 'マイページ・設定'],
  EmailChangeScreen: ['メールアドレス変更', 'マイページ・設定'],
  PasswordChangeScreen: ['パスワード変更', 'マイページ・設定'],
  InquiryScreen: ['お問い合わせ', 'マイページ・設定'],
  WithdrawScreen: ['退会', 'マイページ・設定'],
  AccountDeletedScreen: ['退会完了', 'マイページ・設定'],

  LoginScreen: ['ログイン', '登録・ログイン'],
  SignupScreen: ['新規登録', '登録・ログイン'],
  RegistrationScreen: ['登録', '登録・ログイン'],
  GuestProfileScreen: ['ゲストのプロフィール入力', '登録・ログイン'],
  InvitationExplanationScreen: ['招待の説明', '登録・ログイン'],
  PolicyConsent: ['規約への同意', '登録・ログイン'],
  CircleWaitingIntroScreen: ['はじめの案内', '登録・ログイン'],
};

const EVENTS: Record<string, Entry> = {
  af_login: ['ログイン', '登録・ログイン'],
  af_complete_registration: ['登録完了', '登録・ログイン'],
  family_group_created: ['家族を作成', '家族'],
  family_add_tapped: ['「家族を追加」をタップ', '家族'],
  family_photo_registered: ['家族の写真を登録', '家族'],
  af_invite: ['招待リンクを共有', '家族'],
  family_connected: ['家族とつながった（2人以上）', '家族'],
  birthday_registered: ['誕生日を登録', 'マイページ・設定'],
  diary_post_tapped: ['「日記を書く」をタップ', '日記'],
  diary_created: ['日記を作成', '日記'],
  diary_published: ['日記を公開', '日記'],
  fukumaru_message_sent: ['フクマルにメッセージ', 'ホーム'],
  af_content_view: ['家族のいまを表示', '家族のいま'],
  offer_cta_tapped: ['オファーのボタンをタップ', 'そなえ'],
  offer_coupon_copied: ['クーポンをコピー', 'そなえ'],
  event_cta_tapped: ['イベントのボタンをタップ', 'そなえ'],
  af_share: ['コンテンツを共有', 'そなえ'],
};

export const labelOf = (
  source: KpiAppUsageSource,
  name: string,
): { label: string; category: Category } => {
  const entry = (source === 'ga4' ? SCREENS : EVENTS)[name];

  return entry
    ? { label: entry[0], category: entry[1] }
    : { label: name, category: 'その他' };
};
